import { getTypeNameOfPart } from "../../arrangement/arrangementFunctions";
import { screenPosition } from "./assistantImpl";
import { roundValues } from "oc-threejs/Debugs";

const createProxy: (any) => any = (x) =>
  x?.isSegment
    ? createAccessorProxy(x, segmentAccessors)
    : x?.isEmpty
      ? createAccessorProxy(x, emptyAccessors)
      : x?.isElement
        ? createAccessorProxy(x, elementAccessors)
        : x?.isUMProfil
          ? createAccessorProxy(x, umprofilAccessors)
          : x?.isPfosten
            ? createAccessorProxy(x, pfostenAccessors)
            : undefined;

const proxyCache = new WeakMap();

export function proxy(x: any): any {
  let p = proxyCache.get(x);
  if (!p) {
    p = createProxy(x);
    proxyCache.set(x, p);
  }
  return p;
}

export function unproxy(proxy: any): any {
  return proxy?.[_unproxy_];
}

type Accessor = [(x) => any, (x, v) => any];
type Accessors = Record<string, Accessor>;

const segmentAccessors: Accessors = {
  id: [(x) => x.id, null],
  kanteId: [(x) => x.kanteId, null],
  laenge: [(x) => x.laenge, null],
  verteilungsstrategie: [
    (x) => x.verteilungsstrategieAuswahlwert?.Key,
    (x, v) => (x.verteilungsstrategieAuswahlwert = findAuswahlwert("Verteilungsstrategie", v)),
  ],
  ecken: [(x) => x.ecken.map((v) => ({ x: v.position.x, y: v.position.z })), null],
  type: [() => "Segment", undefined],
  teile: [(x) => x.getPartsAndPfosten().filter(Boolean).map(proxy), null],
  ruler: [(x) => (x.ruler ?? []).filter((r) => r.style).map(mapRuler), null],
  gesamtbreite: [
    (x) => {
      const minMax = x.getMinMax();
      return Math.round((minMax.x1 - minMax.x0) * 1000) / 1000;
    },
    null,
  ],
  toJSON: createToJSON(
    ["id", "kanteId", "laenge", "gesamtbreite", "ecken", "verteilungsstrategie", "type", "teile", "ruler"],
    () => segmentAccessors
  ),
};

const commonAccessors: Accessors = {
  id: [(x) => x.id, undefined],
  type: [getTypeNameOfPart, undefined],
  position3d: [(x) => x.getWorldPosition()?.toJSON(), undefined],
  screenPosition: [(x) => screenPosition(x.getWorldPosition()), undefined],
  size: [(x) => x.size, (x, v) => x.size?.copy(v)],
  breite: [
    (x) => x.size?.x,
    (x, v) => {
      if (x.size) x.size.x = v;
    },
  ],
  höhe: [(x) => x.höhe ?? x.heightPhysical, (x, v) => (x.höhe = v)],
  von: [(x) => (x.isPfostenAnEcke ? 0 : x.segment?.getX(x, 0)), (x, v) => x.segment.setX(x, 0, v)],
  bis: [(x) => (x.isPfostenAnEcke ? 0 : x.segment?.getX(x, 1)), (x, v) => x.segment.setX(x, 1, v)],
  segment: [(x) => (x.isPfostenAnEcke ? null : proxy(x.segment)), undefined],
  segmentId: [(x) => (x.isPfostenAnEcke ? null : x.segment?.id), undefined],
  toJSON: createToJSON(
    ["id", "type", "von", "bis", "position3d", "screenPosition", "breite", "höhe", "segmentId"],
    () => commonAccessors
  ),
};

function createToJSON(props: string[], accessors: () => Accessors): Accessor {
  return [
    (x) => () => roundValues(_.fromPairs(props.map((prop) => [prop, accessors()[prop][0](x)])), 2),
    undefined,
  ];
}

const emptyAccessors = commonAccessors;

const elementAccessors: Accessors = {
  ...commonAccessors,
  form: [
    (x) => x.form?.Key,
    (x, v) => {
      const formObjekt = APP.metadata.Form_Objekt.find((p) => p.Key == v);
      if (!formObjekt) {
        const available = APP.metadata.Form_Objekt.map((p) => p.Key).join(", ");
        throw new Error(`Form "${v}" nicht gefunden. Verfügbar: ${available}`);
      }
      // Find a matching EasyAuswahlwert (massAuswahlwert) for this form
      // via the Mass table, which links Form → EasyAuswahlwert
      const massAuswahlwert = APP.metadata.Mass.filter(
        (m) => m.Form?.Key === v && m.EasyAuswahlwert != null
      )
        .map((m) => m.EasyAuswahlwert)
        .find((a) => !a.Gesperrt);
      if (!massAuswahlwert) {
        throw new Error(`Kein passender Maßtyp für Form "${v}" gefunden`);
      }
      x.change({ massAuswahlwert, füllungAuswahlwert: x.füllungAuswahlwert });
    },
  ],
  höhe: [(x) => x.höhe, (x, v) => (x.höhe = v)],
  füllung: [
    (x) => x.füllungAuswahlwert?.Key,
    (x, v) => {
      const aw = findAuswahlwert("Füllung", v);
      x.change({ füllungAuswahlwert: aw, individualisiert: true });
    },
  ],
  rahmen: [
    (x) => x.rahmenAuswahlwert?.Key,
    (x, v) => (x.rahmenAuswahlwert = findAuswahlwert("Rahmen", v)),
  ],
  gehflügel: [
    (x) => x.gehflügelAuswahlwert?.Key,
    (x, v) => (x.gehflügelAuswahlwert = findAuswahlwert("Gehflügel", v)),
  ],
  drücker: [
    (x) => x.drückerAuswahlwert?.Key,
    (x, v) => (x.drückerAuswahlwert = findAuswahlwert("Drücker", v)),
  ],
  profilzylinder: [
    (x) => x.profilzylinderAuswahlwert?.Key,
    (x, v) => (x.profilzylinderAuswahlwert = findAuswahlwert("Profilzylinder", v)),
  ],
  öffnung: [
    (x) => x.öffnungAuswahlwert?.Key,
    (x, v) => (x.öffnungAuswahlwert = findAuswahlwert("Öffnung", v)),
  ],
  anschlag: [
    (x) => x.anschlagAuswahlwert?.Key,
    (x, v) => (x.anschlagAuswahlwert = findAuswahlwert("Anschlag", v)),
  ],
  füllungsseite: [
    (x) => x.füllungsseiteAuswahlwert?.Key,
    (x, v) => (x.füllungsseiteAuswahlwert = findAuswahlwert("Füllungsseite", v)),
  ],
  anschlagprofil: [
    (x) => x.anschlagprofilAuswahlwert?.Key,
    (x, v) => (x.anschlagprofilAuswahlwert = findAuswahlwert("Anschlagprofil", v)),
  ],
  abschlussleiste: [
    (x) => x.abschlussleisteAuswahlwert?.Key,
    (x, v) => (x.abschlussleisteAuswahlwert = findAuswahlwert("AbschlussleisteOben", v)),
  ],
  profil: [
    (x) => (x.showProfil ? x.profilAuswahlwert?.Key : null),
    (x, v) => {
      x.profilAuswahlwert = findAuswahlwert("Füllung", v);
      x.showProfil = x.profilAuswahlwert != null;
    },
  ],
  profilleiste: [
    (x) => x.profilleisteAuswahlwert?.Key,
    (x, v) => (x.profilleisteAuswahlwert = findAuswahlwert("Profilleiste", v)),
  ],
  eAntrieb: [
    (x) => x.eAntriebAuswahlwert?.Key,
    (x, v) => (x.eAntriebAuswahlwert = findAuswahlwert("E_Antrieb_Vorhanden", v)),
  ],
  basisprofile: [
    (x) => x.profilKategorieAuswahlwert?.Key,
    (x, v) => (x.profilKategorieAuswahlwert = findAuswahlwert("Füllung", v)),
  ],
  profilPosition: [(x) => x.profilPosition, (x, v) => (x.profilPosition = v)],
  profilPositionMin: [(x) => x.sonderprofilMinMax.minPosition, undefined],
  profilPositionMax: [(x) => x.sonderprofilMinMax.maxPosition, undefined],
  showProfil: [(x) => x.showProfil, (x, v) => (x.showProfil = v)],
  anschlussAufRechts: [(x) => x.anschlussAufRechts, (x, v) => (x.anschlussAufRechts = v)],
  gehflügelbreite: [(x) => x.gehflügelbreite, (x, v) => (x.gehflügelbreite = v)],
  standflügelbreite: [(x) => x.standflügelbreite, (x, v) => (x.standflügelbreite = v)],
  torIstFertig: [(x) => x.torIstFertig, (x, v) => (x.torIstFertig = v)],
  toJSON: createToJSON(
    [
      "id",
      "type",
      "von",
      "bis",
      "position3d",
      "screenPosition",
      "breite",
      "höhe",
      "segmentId",
      "form",
      "füllung",
      "rahmen",
      "gehflügel",
      "drücker",
      "profilzylinder",
      "öffnung",
      "anschlag",
      "füllungsseite",
      "anschlagprofil",
      "abschlussleiste",
      "profil",
      "profilleiste",
      "basisprofile",
      "eAntrieb",
      "torIstFertig",
    ],
    () => elementAccessors
  ),
};

const pfostenAccessors: Accessors = {
  ...commonAccessors,
  pfostenfarbe: [
    (x) => x.pfostenfarbeAuswahlwert?.Key,
    (x, v) => (x.pfostenfarbeAuswahlwert = findAuswahlwert("Pfostenfarbe", v)),
  ],
  bodenbefestigung: [
    (x) => x.bodenbefestigungAuswahlwert?.Key,
    (x, v) => (x.bodenbefestigungAuswahlwert = findAuswahlwert("Bodenbefestigung", v)),
  ],
  pfostenart: [
    (x) => x.pfostenartAuswahlwert?.Key,
    (x, v) => (x.pfostenartAuswahlwert = findAuswahlwert("Pfostenart", v)),
  ],
  montageadapter: [
    (x) => x.montageadapterAuswahlwert?.Key,
    (x, v) => (x.montageadapterAuswahlwert = findAuswahlwert("Montageadapter", v)),
  ],
  verbundanker: [
    (x) => x.verbundankerAuswahlwert?.Key,
    (x, v) => (x.verbundankerAuswahlwert = findAuswahlwert("Verbundanker", v)),
  ],
  ummantelung: [
    (x) => x.ummantelungAuswahlwert?.Key,
    (x, v) => (x.ummantelungAuswahlwert = findAuswahlwert("Ummantelung", v)),
  ],
  anker: [
    (x) => x.ankerAuswahlwert?.Key,
    (x, v) => (x.ankerAuswahlwert = findAuswahlwert("Anker", v)),
  ],
  ankerInnen: [(x) => x.ankerInnen, (x, v) => (x.ankerInnen = v)],
  ausgleichGefälle: [(x) => x.ausgleichGefälle, (x, v) => (x.ausgleichGefälle = v)],
  toJSON: createToJSON(
    [
      "id",
      "type",
      "von",
      "bis",
      "position3d",
      "screenPosition",
      "breite",
      "segmentId",
      "pfostenfarbe",
      "bodenbefestigung",
      "pfostenart",
      "montageadapter",
      "verbundanker",
      "ummantelung",
      "anker",
      "ankerInnen",
      "ausgleichGefälle",
    ],
    () => pfostenAccessors
  ),
};

const umprofilAccessors: Accessors = {
  ...commonAccessors,
  pfostenfarbe: [
    (x) => x.pfostenfarbeAuswahlwert?.Key,
    (x, v) => (x.pfostenfarbeAuswahlwert = findAuswahlwert("Pfostenfarbe", v)),
  ],
  toJSON: createToJSON(
    [
      "id",
      "type",
      "von",
      "bis",
      "position3d",
      "screenPosition",
      "breite",
      "segmentId",
      "pfostenfarbe",
    ],
    () => umprofilAccessors
  ),
};

function findAuswahlwert(auswahlKey: string, key: string) {
  const wert = APP.metadata.Auswahlwert.find((a) => a.Auswahl?.Key === auswahlKey && a.Key === key);
  if (wert == null) {
    throw new Error(`${auswahlKey} "${key}" nicht gefunden`);
  }
  return wert;
}

function mapRuler(ruler) {
  return {
    x0: ruler.x0,
    x1: ruler.x1,
    breite: Math.round((ruler.x1 - ruler.x0) * 1000) / 1000,
    style: ruler.style,
  };
}

const _unproxy_ = Symbol();

function createAccessorProxy(target: any, propertyAccessors: Accessors) {
  return new Proxy(target, {
    get(target, prop: string | symbol, receiver) {
      if (prop === _unproxy_) {
        return target;
      }
      if (prop === Symbol.iterator) {
        return function* () {
          for (const key of Object.keys(propertyAccessors)) {
            yield { key, value: target[key] };
          }
        };
      }
      if (prop === "set") {
        return (other: Record<string, any>) => {
          Object.keys(other).forEach((key) => {
            const accessor = propertyAccessors[key];
            if (accessor?.[1] !== undefined) {
              accessor[1](target, other[key]);
              APP.arrangement.update3d();
            }
          });
        };
      }
      if (typeof prop === "string") {
        const accessor = propertyAccessors[prop];
        if (accessor == undefined) {
          return undefined;
        }
        return accessor[0](target);
      }
      return undefined;
    },
    set(target, prop, value, receiver) {
      if (typeof prop === "string") {
        const accessor = propertyAccessors[prop];
        if (accessor == undefined) {
          return false;
        }
        accessor[1](target, value);
        APP.arrangement.update3d();
        return true;
      }
      return false;
    },
    ownKeys(target) {
      return Object.keys(propertyAccessors);
    },
    getOwnPropertyDescriptor(target, prop) {
      if (propertyAccessors.hasOwnProperty(prop)) {
        return {
          enumerable: true,
          configurable: true,
        };
      }
      return undefined;
    },
  });
}
